import {
    SimpleEvent,
    SimpleDepartment,
    Client,
    DeliveryMethod,
    SimpleSeatmapSection,
    ClientDonation,
} from "./Event";
import {
    CartItem,
    CartItemServiceFee,
    CartItemDiscount,
    CartTax,
    DiscountCategory,
    CartItemRedemption,
} from "./Cart";
import { SimpleUser } from "./User";
import { SalesContext } from "./SalesContext";
import {
    EventPackage,
    EventPackagePriceGroup,
    SimpleSectionSeat,
} from "./AssignedSeating";

export enum PaymentMethod {
    CASH = "CASH",
    DEBIT = "DEBIT",
    CREDIT_MC = "CREDIT_MC",
    CREDIT_VISA = "CREDIT_VISA",
    CREDIT_AMEX = "CREDIT_AMEX",
    CREDIT_OTHER = "CREDIT_OTHER",
    DEBIT_CREDIT = "DEBIT_CREDIT",
    CHEQUE = "CHEQUE",
    INVOICE = "INVOICE",
    ELECTRONIC_PAYMENT = "ELECTRONIC_PAYMENT",
}

export enum PaymentMedium {
    OFFLINE = "OFFLINE",
    ONLINE_GATEWAY = "ONLINE_GATEWAY",
    SQUARE_POS = "SQUARE_POS",
}

export enum OrderStatus {
    PURCHASED = "PURCHASED",
    PAYMENT_PENDING = "PAYMENT_PENDING",
    PAYMENT_FAILED = "PAYMENT_FAILED",
    REFUNDED = "REFUNDED",
}

export enum TransactionLogStatus {
    SUCCESS = "SUCCESS",
    GENERAL_FAILURE = "GENERAL_FAILURE",
    DECLINED = "DECLINED",
    PROCESSING_ERROR = "PROCESSING_ERROR",
}

export enum AddressType {
    SHIPPING = "SHIPPING",
}

export interface SimpleOrder {
    id: number;
    dateCreated: number;
    status: OrderStatus;
    salesContext: SalesContext;
    orderAddress: OrderAddress;
}

export interface OrderPackageItem {
    id: number;
    comp: boolean;
    seatId: number | null;
    sectionId: number | null;
    dateCreated: number;
    event: SimpleEvent;
    applicableTaxIds: Array<number>;
    seat: SimpleSectionSeat | null;
    section: SimpleSeatmapSection | null;
    eventPackage: EventPackage;
    eventPackagePriceGroup: EventPackagePriceGroup;
    serviceFees: Array<OrderItemServiceFee>;
    discount: OrderItemDiscount | null;
    amount: number;
}

export interface OrderDonation {
    id: number;
    amount: number;
    donation: ClientDonation;
}

export interface Order extends SimpleOrder {
    retrievalKey: string;
    // Null for ecommerce orders
    user: SimpleUser | null;

    authFee: number;
    items: Array<OrderItem>;
    packages: Array<OrderPackageItem>;
    taxes: Array<OrderTax>;
    subtotal: number;
    total: number;
    client: Client;
    transactionLogs: Array<TransactionLog>;

    // These are optional values extracted from the saved metadata
    nodeNumber?: string | null;
    amountTendered?: string | null;

    // Stripe specific
    paymentIntent?: PaymentIntent | null;
    deliveryMethod: OrderDeliveryMethod | null;

    donations: Array<OrderDonation>;
}

export interface OrderAddress {
    firstName: string;
    lastName: string;
    address: string;
    address2?: string; // Always optional
    city: string;
    stateId: number | null;
    stateName?: string;
    countryId: number | null;
    countryName?: string;
    phone: string;
    postal: string;
    email: string;
    emailConsent: boolean;
}

export interface OtherAddress {
    firstName: string;
    lastName: string;
    address: string;
    address2?: string; // Always optional
    city: string;
    stateId: number | null;
    stateName?: string;
    countryId: number | null;
    countryName?: string;
    phone: string;
    postal: string;
    type: AddressType;
}

export interface OrderItem extends CartItem {
    orderItemId: number;

    barcodeId: number;
    barcode: string;

    seatName: string;
    seatRowName: string;
    seatSectionName: string;
    seatSectionType: string;
    seatRowType: string;

    // @override
    serviceFees: Array<OrderItemServiceFee>;
    // @override
    discount: OrderItemDiscount | null;
    redemption: OrderItemRedemption | null;

    // These are saved with the order
    amount: number;
    serviceFeeExempt: boolean;

    calculatedMetadata?: { [key: string]: string };

    recipients: Array<OrderItemRecipient>;
}

export enum OrderItemRecipientType {
    EMAIL = "EMAIL",
    SMS = "SMS",
}

export interface OrderItemRecipient {
    id: number;
    orderItemId: number;
    type: OrderItemRecipientType;
    name: string;
    destination: string;
    dateSent: number;
}

export interface OrderItemServiceFee extends CartItemServiceFee {
    orderItemServiceFeeId: number;
}

export interface OrderItemDiscount extends CartItemDiscount {
    orderItemDiscountId: number;
}

export interface OrderItemRedemption extends CartItemRedemption {
    orderItemRedemptionId: number;
}

export interface OrderTax extends CartTax {
    orderTaxId: number;
}

export interface TransactionLog {
    id: number;
    amount: number;
    transactionType: number;
    paymentMethod: PaymentMethod;
    paymentMedium: PaymentMedium;
    squarePayment: string | null;
    transactionDate: number;
    status: TransactionLogStatus;
    // KB August 11, 2020: Deprecated on back-end. This was the old PHP serialized value.
    // response: string;
    responseText: string;
}

export interface PaymentIntent {
    paymentIntentToken: string;
}

export interface OrderDeliveryMethod {
    id: number;
    amount: number;
    deliveryMethod: DeliveryMethod;
}

export const EmptyAddress = (): OrderAddress => {
    return {
        firstName: "",
        lastName: "",
        address: "",
        address2: "",
        city: "",
        stateId: null,
        countryId: null,
        phone: "",
        postal: "",
        email: "",
        emailConsent: false,
    };
};
